/*eslint max-lines-per-function: ["error", 185]*/
import React, { useEffect, useState } from 'react';
import { Modal, Form } from 'react-bootstrap';

import ValidationAlert from '../../../app/components/ValidationAlert';
import Button from '../../../common/components/Button/Button';
import { usePokCore } from '../../../common/hooks/usePokCore';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import { useTranslation } from '../../../common/hooks/useTranslation';
import QuillTextEditor from '../../../app/components/QuillTextEditor';
import FormRow from '../../../app/components/FormRow';
import DragNDropFiles, {
  NewFile,
  DragNDropFile,
} from '../../../common/components/File/DragNDropFiles';
import { CreateUpdateAttachmentDtoEnObjectTypeEnum } from '../../../common/pokCore/autogenerated/pokApiClient';
import { convertUploadedFilesToAttachments } from '../../../common/components/File/DragNDropUtils';

interface EmailTextSubjectEditorProps {
  show: boolean;
  invoiceId: string;
  onClose: () => void;
  emailText?: string | null;
  emailSubject?: string | null;
  existingFiles?: DragNDropFile[];
}

export const EmailTextSubjectEditor: React.FC<
  EmailTextSubjectEditorProps
> = props => {
  const pok = usePokCore();
  const { t, tk } = useTranslation('fvsLng');
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [emailText, setEmailText] = useState<string | undefined>(
    props.emailText || '',
  );
  const [emailSubject, setEmailSubject] = useState<string>(
    props.emailSubject || '',
  );
  const [filesToAdd, setFilesToAdd] = useState<NewFile[]>([]);
  const [filesToDelete, setFilesToDelete] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [maxFileSizeExceeded, setMaxFileSizeExceeded] = useState(false);

  const onAdd = async () => {
    if (
      (!emailText || emailText === '') &&
      (!emailSubject || emailSubject === '') &&
      filesToAdd.length === 0 &&
      (props.existingFiles?.length === 0 ||
        props.existingFiles?.length === filesToDelete.length)
    ) {
      setShowSaveErrors(true);
      setSaveErrors([t(tk.sendEmail.emailTextSubjectRequired)]);
      return;
    }
    if (maxFileSizeExceeded) {
      setShowSaveErrors(true);
      setSaveErrors([t(tk.attachment.fileSizeExceeded)]);
      return;
    }
    setSending(true);
    if (emailText !== undefined || emailSubject !== undefined) {
      await pok.salesInvoices
        .updateEmailText(props.invoiceId, emailText, emailSubject)
        .catch(errorResponse => {
          setShowSaveErrors(true);
          setSaveErrors(errorResponse.errors);
          setSending(false);
          return;
        });
    }
    if (filesToAdd.length > 0 || filesToDelete.length > 0) {
      const attachments = convertUploadedFilesToAttachments(
        filesToAdd,
        filesToDelete,
        props.invoiceId,
        CreateUpdateAttachmentDtoEnObjectTypeEnum.SalesInvoice,
      );

      await pok.attachments
        .addDeleteAttachments(attachments)
        .catch(errorResponse => {
          setShowSaveErrors(true);
          setSaveErrors(errorResponse.errors);
          setSending(false);
          return;
        });
    }
    setSending(false);
    handleClose();
  };

  useEffect(() => {
    setEmailText(props.emailText || '');
    setEmailSubject(props.emailSubject || '');
  }, [pok, props.emailText, props.emailSubject, props.invoiceId, props.show]);

  const handleClose = () => {
    setEmailText('');
    setEmailSubject('');
    setFilesToAdd([]);
    setFilesToDelete([]);
    setSaveErrors([]);
    setShowSaveErrors(false);
    props.onClose();
  };

  const handleDelete = async () => {
    await pok.salesInvoices
      .updateEmailText(props.invoiceId, undefined, undefined)
      .catch(errorResponse => {
        setShowSaveErrors(true);
        setSaveErrors(errorResponse.errors);
        return;
      });
    if (props.existingFiles && props.existingFiles.length > 0) {
      await pok.attachments
        .addDeleteAttachments({
          attachmentsToAdd: [],
          attachmentsToDelete: props.existingFiles.map(f => f.id),
        })
        .catch(errorResponse => {
          setShowSaveErrors(true);
          setSaveErrors(errorResponse.errors);
          return;
        });
    }
    handleClose();
  };

  return (
    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
    <div onClick={e => e.stopPropagation()}>
      <Modal
        show={props.show}
        onHide={handleClose}
        centered={true}
        backdrop="static"
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {!props.emailText
              ? t(tk.sendEmail.newEmailNote)
              : t(tk.sendEmail.editEmailNote)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="d-grid gap-4 mb-3">
            <FormRow controlId="subject" label={t(tk.sendEmail.subject)}>
              <Form.Control
                type="text"
                value={emailSubject}
                onChange={e => setEmailSubject(e.target.value)}
              />
            </FormRow>
            <FormRow controlId="message" label={t(tk.sendEmail.message)}>
              <QuillTextEditor
                value={emailText}
                onChange={val => setEmailText(val)}
                uniqueKey="emailMessageSendInvoice"
              />
            </FormRow>
            <FormRow controlId="files" label={t(tk.sendEmail.fileUpload)}>
              <DragNDropFiles
                existingFiles={props.existingFiles}
                setFilesToDelete={setFilesToDelete}
                setFilesToAdd={setFilesToAdd}
                setMaxFileSizeExceeded={setMaxFileSizeExceeded}
              />
            </FormRow>
          </Form>
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="mt-3"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleClose}>
            {t(tk.button.close)}
          </Button>
          {(props.emailText ||
            props.emailSubject ||
            (props.existingFiles && props.existingFiles?.length > 0)) && (
            <ConfirmationButton
              variant="outline-danger"
              onOK={handleDelete}
              confirmation={t(tk.sendEmail.askDeleteEmailText)}
            >
              {t(tk.button.delete)}
            </ConfirmationButton>
          )}
          <Button
            variant="outline-primary"
            onClick={onAdd}
            isLoading={sending}
            disabled={sending}
          >
            {t(tk.button.save)}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
